import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
    getRentalFAQs,
    createRentalFAQ,
    deleteRentalFAQ,
} from "../rentalService";

const RentalFAQ = ({ rentalId }) => {
    const queryClient = useQueryClient();
    const [question, setQuestion] = useState("");
    const [answer, setAnswer] = useState("");
    const [faqError, setFaqError] = useState("");

    const { data: faqData, isLoading } = useQuery({
        queryKey: ["rentalFAQs", rentalId],
        queryFn: () => getRentalFAQs(rentalId),
        enabled: !!rentalId,
    });

    const faqs = faqData?.data || [];

    const createMutation = useMutation({
        mutationFn: createRentalFAQ,
        onSuccess: () => {
            queryClient.invalidateQueries(["rentalFAQs", rentalId]);
            setQuestion("");
            setAnswer("");
        }
    });

    const deleteMutation = useMutation({
        mutationFn: deleteRentalFAQ,
        onSuccess: () => {
             queryClient.invalidateQueries(["rentalFAQs", rentalId]);
        }
    });

    const handleAdd = () => {
        setFaqError("");
        const q = String(question || "").trim();
        const a = String(answer || "").trim();
        if (q.length < 3) {
            setFaqError("Question must be at least 3 characters.");
            return;
        }
        if (!a) {
            setFaqError("Answer is required.");
            return;
        }
        if (q.length > 2000 || a.length > 10000) {
            setFaqError("Question (max 2000) or answer (max 10000 characters) is too long.");
            return;
        }
        createMutation.mutate({
            rental_item_id: rentalId,
            question: q,
            answer: a,
        });
    };

    return (
        <div className="space-y-6">
            <div className="rounded-xl border border-amber-100 bg-amber-50/60 px-4 py-3 flex gap-3">
                <i className="fi fi-rr-info text-amber-600 mt-0.5 shrink-0" aria-hidden />
                <p className="text-xs text-amber-900/80 leading-relaxed">
                    Optional step — add frequently asked questions for renters. Use{" "}
                    <span className="font-semibold">Skip</span> at the bottom if you do not need FAQs.
                </p>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <h2 className="text-lg font-semibold mb-4 text-gray-800">Add FAQ</h2>
                {faqError ? <p className="text-sm text-red-600 mb-2">{faqError}</p> : null}
                <div className="space-y-3">
                    <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Question</label>
                        <input
                            type="text"
                            value={question}
                            onChange={(e) => {
                                setFaqError("");
                                setQuestion(e.target.value);
                            }}
                            placeholder="e.g. What is the battery life?"
                            className="w-full border rounded-lg p-2"
                            maxLength={2000}
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Answer</label>
                        <textarea
                            value={answer}
                            onChange={(e) => {
                                setFaqError("");
                                setAnswer(e.target.value);
                            }}
                            placeholder="e.g. Approx 4 hours of continuous shooting."
                            rows={3}
                            className="w-full border rounded-lg p-2"
                            maxLength={10000}
                        />
                    </div>
                </div>
                <button onClick={handleAdd} className="mt-4 bg-primary text-white px-4 py-2 rounded-lg text-sm">Add FAQ</button>
            </div>

            <div className="space-y-3">
                {faqs.map(faq => (
                    <div key={faq.id} className="p-4 bg-white border border-gray-100 rounded-lg shadow-sm flex items-start justify-between gap-4">
                        <div>
                            <p className="font-semibold text-gray-900">{faq.question}</p>
                            <p className="text-sm text-gray-600 mt-1">{faq.answer}</p>
                        </div>
                        <button onClick={() => deleteMutation.mutate(faq.id)} className="text-red-500 hover:text-red-700">
                            <i className="fi fi-rr-trash"></i>
                        </button>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default RentalFAQ;
